import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Question } from '../../types/test';
import { Brain, CheckCircle } from 'lucide-react';

interface QuestionRendererProps {
  question: Question;
  questionNumber: number;
  totalQuestions: number;
  onAnswer: (answer: string | number, timeSpent: number) => void;
  selectedAnswer?: string | number | null;
}

const QuestionRenderer: React.FC<QuestionRendererProps> = ({
  question,
  questionNumber,
  totalQuestions,
  onAnswer,
  selectedAnswer,
}) => {
  const [localSelectedAnswer, setLocalSelectedAnswer] = useState<
    string | number | null
  >(selectedAnswer || null);
  const [startTime] = useState(Date.now());

  const handleAnswerSelect = (answer: string | number) => {
    setLocalSelectedAnswer(answer);
    const currentTimeSpent = Date.now() - startTime;
    onAnswer(answer, currentTimeSpent);
  };

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'easy':
        return 'from-green-400 to-emerald-400';
      case 'medium':
        return 'from-yellow-400 to-orange-400';
      case 'hard':
        return 'from-red-400 to-pink-400';
      default:
        return 'from-blue-400 to-cyan-400';
    }
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'verbal':
        return '📝';
      case 'mathematical':
        return '🔢';
      case 'spatial':
        return '🔷';
      case 'logical':
        return '🧩';
      case 'memory':
        return '🧠';
      default:
        return '❓';
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.5 }}
      className="w-full max-w-4xl mx-auto"
    >
      {/* Question Header - Hidden on mobile, shown on desktop */}
      <div className="hidden lg:block bg-white/90 backdrop-blur-md rounded-3xl p-6 mb-6 shadow-xl border border-white/20">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center">
              <Brain size={24} className="text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-800">
                Question {questionNumber} of {totalQuestions}
              </h2>
              <div className="flex items-center gap-3 mt-1">
                <span
                  className={`px-3 py-1 rounded-full text-xs font-medium text-white bg-gradient-to-r ${getDifficultyColor(question.difficulty)}`}
                >
                  {question.difficulty.charAt(0).toUpperCase() +
                    question.difficulty.slice(1)}
                </span>
                <span className="flex items-center gap-1 text-sm text-gray-600">
                  {getCategoryIcon(question.category)}
                  {question.category.charAt(0).toUpperCase() +
                    question.category.slice(1)}
                </span>

              </div>
            </div>
          </div>


        </div>

        {/* Progress Bar */}
        <div className="w-full bg-gray-200 rounded-full h-2 mb-4">
          <motion.div
            className="bg-gradient-to-r from-purple-500 to-pink-500 h-2 rounded-full"
            initial={{ width: 0 }}
            animate={{ width: `${(questionNumber / totalQuestions) * 100}%` }}
            transition={{ duration: 0.5 }}
          />
        </div>
      </div>

      {/* Question Content */}
      <div className="bg-white/90 backdrop-blur-md rounded-2xl sm:rounded-3xl p-4 sm:p-6 md:p-8 shadow-xl border border-white/20">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
        >
          <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-800 mb-4 sm:mb-6 leading-relaxed">
            {question.question}
          </h3>

          {/* Pattern Recognition Display */}
          {question.type === 'pattern-recognition' && question.pattern && (
            <div className="mb-6 sm:mb-8 p-4 sm:p-6 bg-gradient-to-r from-gray-50 to-blue-50 rounded-xl sm:rounded-2xl">
              <div className="grid grid-cols-4 gap-2 sm:gap-3 md:gap-4 max-w-xs sm:max-w-md mx-auto">
                {question.pattern.sequence.map((item, index) => (
                  <motion.div
                    key={index}
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: index * 0.1 }}
                    className={`w-16 h-16 sm:w-18 sm:h-18 md:w-20 md:h-20 rounded-lg sm:rounded-xl flex items-center justify-center text-lg sm:text-xl md:text-2xl font-bold text-white ${
                      index === question.pattern!.missing
                        ? 'bg-gradient-to-r from-gray-300 to-gray-400 border-2 border-dashed border-gray-500'
                        : 'bg-gradient-to-r from-purple-400 to-pink-400'
                    }`}
                  >
                    {index === question.pattern!.missing ? '?' : item}
                  </motion.div>
                ))}
              </div>
            </div>
          )}

          {/* Image Display */}
          {question.image && (
            <div className="mb-6 sm:mb-8 text-center">
              <img
                src={question.image}
                alt="Question illustration"
                className="max-w-full h-auto rounded-xl sm:rounded-2xl shadow-lg mx-auto"
              />
            </div>
          )}

          {/* Answer Options */}
          {question.options && (
            <div className="grid grid-cols-1 gap-3 sm:gap-4">
              <AnimatePresence>
                {question.options.map((option, index) => (
                  <motion.button
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.1 * index }}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => handleAnswerSelect(option)}
                    className={`p-3 sm:p-4 rounded-xl sm:rounded-2xl border-2 transition-all duration-300 text-left relative overflow-hidden ${
                      localSelectedAnswer === option
                        ? 'border-purple-500 bg-gradient-to-r from-purple-50 to-pink-50 text-purple-700 shadow-lg'
                        : 'border-gray-200 hover:border-purple-300 hover:bg-purple-50/50 hover:shadow-md'
                    }`}
                  >
                    <div className="flex items-center gap-3 relative z-10">
                      <div
                        className={`w-5 h-5 sm:w-6 sm:h-6 rounded-full border-2 flex items-center justify-center transition-all duration-300 flex-shrink-0 ${
                          localSelectedAnswer === option
                            ? 'border-purple-500 bg-purple-500'
                            : 'border-gray-300'
                        }`}
                      >
                        {localSelectedAnswer === option && (
                          <motion.div
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-white rounded-full"
                          />
                        )}
                      </div>
                      <span className="font-medium flex-1 text-sm sm:text-base">{option}</span>
                      {localSelectedAnswer === option && (
                        <motion.div
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                          className="text-purple-500 flex-shrink-0"
                        >
                          <CheckCircle size={18} className="sm:w-5 sm:h-5" />
                        </motion.div>
                      )}
                    </div>

                    {/* Animated background */}
                    {localSelectedAnswer === option && (
                      <motion.div
                        initial={{ scale: 0, opacity: 0 }}
                        animate={{ scale: 1, opacity: 0.1 }}
                        className="absolute inset-0 bg-gradient-to-r from-purple-500 to-pink-500 rounded-xl sm:rounded-2xl"
                      />
                    )}
                  </motion.button>
                ))}
              </AnimatePresence>
            </div>
          )}


        </motion.div>
      </div>
    </motion.div>
  );
};

export default QuestionRenderer;
